<template>
  <v-dialog :value="value" persistent max-width="1400px" scrollable>
    <!-- Results Dialog -->
    <v-dialog v-model="resultsDialog" persistent max-width="1200px" scrollable>
      <v-card v-if="resultsDialog">
        <v-card-title>Batch Create Results</v-card-title>
        <v-card-text>
          <!-- Summary -->
          <v-alert
            v-if="resultApps.length && !resultFailed.length"
            type="success"
            dense
            class="mb-4"
          >
            All {{ resultApps.length }} application(s) were created successfully!
          </v-alert>
          <v-alert v-if="resultFailed.length" type="error" dense class="mb-4">
            Unable to create applications. Please review the errors below and try again.
          </v-alert>

          <!-- Created Applications -->
          <v-data-table
            v-if="resultApps.length"
            :headers="resultHeaders"
            :items="resultApps"
            :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
            class="elevation-1 mb-4"
          >
            <template #item.id="{ item }">
              <router-link :to="appPath(item.id)">{{ item.id }}</router-link>
            </template>
          </v-data-table>

          <!-- Failed Applications -->
          <v-expansion-panels v-if="resultFailed.length" flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="error--text">
                <span>
                  <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                  Failed Applications ({{ resultFailed.length }})
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card v-for="(fail, i) in resultFailed" :key="i" outlined class="mb-2 pa-3">
                  <div class="text-subtitle-2">{{ fail.name || `Application ${i + 1}` }}</div>
                  <div class="text-caption error--text">{{ friendlyError(fail.error) }}</div>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" @click="closeResults">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Form -->
    <nv-form
      :errors="formErrors"
      :loading="loading"
      title="Batch Create Applications"
      data-cy="batch-create-form"
      @cancel="formCancel"
      @nv-submit="formSubmit"
    >
      <v-alert v-if="validationError" type="error" dense class="mb-2">
        {{ validationError }}
        <a
          v-if="allFailingRows.length > 5"
          href="#"
          class="white--text font-weight-bold"
          @click.prevent="errorsDialog = true"
        >
          View all {{ allFailingRows.length }} errors
        </a>
      </v-alert>

      <v-dialog v-model="errorsDialog" max-width="600px" scrollable>
        <v-card>
          <v-card-title class="error white--text text-subtitle-1">
            <v-icon left color="white">mdi-alert-circle</v-icon>
            {{ allFailingRows.length }} Application(s) with Errors
          </v-card-title>
          <v-card-text class="pa-0" style="max-height: 400px;">
            <v-list dense>
              <v-list-item v-for="(row, i) in allFailingRows" :key="i">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="error">mdi-close-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ row }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="errorsDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <template-selector v-if="value" ref="template-selector" v-model="templateId" />

      <owner-search v-if="!ownerId" v-model="selectedOwnerId" />

      <v-divider class="my-4" />

      <v-row dense align="center" class="mb-2">
        <v-col cols="auto">
          <span class="text-subtitle-2">Applications ({{ applications.length }}/100)</span>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn x-small text @click="pasteDialog = true">
            <v-icon x-small left>mdi-content-paste</v-icon> Paste
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn x-small text @click="$refs.fileInput.click()">
            <v-icon x-small left>mdi-file-upload</v-icon> Import File
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            style="display: none;"
            @change="handleFileImport"
          />
        </v-col>
        <v-col cols="auto">
          <v-btn x-small color="secondary" :disabled="applications.length >= 100" @click="addRow">
            <v-icon x-small left>mdi-plus</v-icon> Add Row
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn x-small color="error" :disabled="applications.length <= 1" @click="clearAll">
            <v-icon x-small left>mdi-delete-sweep</v-icon> Clear All
          </v-btn>
        </v-col>
      </v-row>

      <v-simple-table dense>
        <thead>
          <tr>
            <th>#</th>
            <th>Name *</th>
            <th>Description</th>
            <th>Sub-brand/VC-ID *</th>
            <th>Website URL</th>
            <th>Support URL</th>
            <th>Store URL</th>
            <th>Client ID*</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(app, i) in applications" :key="i">
            <td class="text-caption">{{ i + 1 }}</td>
            <td>
              <v-text-field
                v-model="app.name"
                :rules="[
                  v => !!v || 'Required',
                  v => !v || v.length <= 255 || 'Max 255 characters',
                  v => !v || nameRegex.test(v) || 'Must be valid name format',
                ]"
                :error-messages="app._errors && app._errors.name ? [app._errors.name] : []"
                counter="255"
                maxlength="255"
                dense
                single-line
                hide-details="auto"
              />
            </td>
            <td>
              <v-text-field
                v-model="app.description"
                :rules="[
                  v => !v || v.length <= 255 || 'Max 255 characters',
                  v => !v || descRegex.test(v) || 'Must be valid description format',
                ]"
                :error-messages="
                  app._errors && app._errors.description ? [app._errors.description] : []
                "
                counter="255"
                maxlength="255"
                dense
                single-line
                hide-details="auto"
              />
            </td>
            <td>
              <sub-brand-selector
                v-if="!app.sub_brand"
                :value="app.ddrs_entity_id"
                @input="setSubBrand(i, $event)"
              />
              <v-text-field
                v-else
                v-model="app.sub_brand"
                :error-messages="
                  app._errors && app._errors.sub_brand ? [app._errors.sub_brand] : []
                "
                dense
                single-line
                hide-details="auto"
                clearable
              />
            </td>
            <td>
              <v-text-field
                v-model="app.website_url"
                :error-messages="
                  app._errors && app._errors.website_url ? [app._errors.website_url] : []
                "
                dense
                single-line
                hide-details="auto"
              />
            </td>
            <td>
              <v-text-field
                v-model="app.support_url"
                :error-messages="
                  app._errors && app._errors.support_url ? [app._errors.support_url] : []
                "
                dense
                single-line
                hide-details="auto"
              />
            </td>
            <td>
              <v-text-field
                v-model="app.store_url"
                :error-messages="
                  app._errors && app._errors.store_url ? [app._errors.store_url] : []
                "
                dense
                single-line
                hide-details="auto"
              />
            </td>
            <td>
              <v-text-field
                v-model="app.client_id"
                :error-messages="
                  app._errors && app._errors.client_id ? [app._errors.client_id] : []
                "
                dense
                single-line
                hide-details="auto"
              />
            </td>
            <td>
              <v-btn icon x-small :disabled="applications.length <= 1" @click="removeRow(i)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </nv-form>

    <!-- Paste Dialog -->
    <v-dialog v-model="pasteDialog" max-width="500px">
      <v-card>
        <v-card-title>Paste from Spreadsheet</v-card-title>
        <v-card-text>
          <p class="text-caption text--secondary">
            Tab-separated columns: Name, Description, Sub-brand, Website URL, Support URL, Store
            URL, Client ID
          </p>
          <v-textarea v-model="pasteText" outlined rows="6" placeholder="Paste rows here..." />
          <v-alert v-if="pasteError" type="error" dense class="mt-2">{{ pasteError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closePaste">Cancel</v-btn>
          <v-btn color="secondary" :disabled="!pasteText.trim()" @click="parsePaste">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import HTTP from '@/api/dog';
import { FormMixin } from '@nielsen-media/ui-toolkit-vue-api';
import { read, utils } from 'xlsx';
import TemplateSelector from '@/components/applications/TemplateSelector.vue';
import OwnerSearch from '@/components/applications/OwnerSearch.vue';
import SubBrandSelector from '@/components/applications/SubBrandSelector.vue';

const resultHeaders = [
  { text: 'ID', value: 'id', sortable: false },
  { text: 'Name', value: 'name', sortable: true },
  { text: 'Description', value: 'description', sortable: false },
];

const URL_REGEX = /^https?:\/\/.+/;
const NAME_REGEX = /^[a-zA-Z0-9(]([^\t\n\b]*[a-zA-Z0-9*.!)])?$/;
const DESC_REGEX = /^[a-zA-Z0-9(]([^\t\n\b]*[a-zA-Z0-9*.!)#])?$/;

function emptyRow() {
  return {
    name: '',
    description: '',
    sub_brand: '',
    ddrs_entity_id: null,
    website_url: '',
    support_url: '',
    store_url: '',
    client_id: '',
    _errors: {},
  };
}

export default {
  components: { TemplateSelector, OwnerSearch, SubBrandSelector },
  mixins: [FormMixin],
  props: {
    ownerId: { type: [Number, String], default: null },
  },
  data: () => ({
    templateId: null,
    selectedOwnerId: null,
    applications: [emptyRow()],
    nameRegex: NAME_REGEX,
    descRegex: DESC_REGEX,

    pasteDialog: false,
    pasteText: '',
    pasteError: '',
    resultsDialog: false,
    resultApps: [],
    resultFailed: [],
    resultHeaders,
    validationError: '',
    allFailingRows: [],
    errorsDialog: false,
  }),
  computed: {
    resolvedOwnerId() {
      return this.ownerId || this.selectedOwnerId;
    },
    duplicateNames() {
      const names = this.applications.map(a => (a.name || '').trim().toLowerCase()).filter(Boolean);
      return [...new Set(names.filter((n, i) => names.indexOf(n) !== i))];
    },
  },
  methods: {
    addRow() {
      if (this.applications.length < 100) this.applications.push(emptyRow());
    },
    removeRow(i) {
      this.applications.splice(i, 1);
    },
    setSubBrand(i, ddrsEntityId) {
      this.applications[i].ddrs_entity_id = ddrsEntityId;
      if (ddrsEntityId) {
        this.applications[i].sub_brand = '';
        HTTP.get(`/ddrs_entities/${ddrsEntityId}`).then(res => {
          if (res.data && res.data.client_id) {
            this.$set(this.applications[i], 'client_id', res.data.client_id);
          }
        });
      }
    },
    clearAll() {
      this.applications = [emptyRow()];
      this.validationError = '';
    },
    closePaste() {
      this.pasteText = '';
      this.pasteError = '';
      this.pasteDialog = false;
    },
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const wb = read(e.target.result, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = utils.sheet_to_json(sheet, { header: 1, defval: '' });
          if (rows.length < 2) {
            this.$flash('error', 'File must have a header row and at least one data row.');
            return;
          }
          const dataRows = rows.slice(1).filter(r => r.some(c => String(c).trim()));
          if (dataRows.length > 100) {
            this.$flash(
              'error',
              'Maximum 100 rows allowed per import. Please reduce the number of rows.',
            );
            return;
          }
          const headerMap = {};
          const aliases = {
            name: ['name'],
            description: ['description', 'desc'],
            sub_brand: ['sub_brand', 'sub-brand', 'subbrand', 'vc-id', 'vcid', 'sub-brand/vc-id'],
            website_url: ['website_url', 'website url', 'website'],
            support_url: ['support_url', 'support url', 'support'],
            store_url: ['store_url', 'store url', 'store'],
            client_id: ['client_id', 'client id', 'clientid'],
          };
          rows[0].forEach((col, i) => {
            const norm = String(col)
              .trim()
              .toLowerCase();
            Object.entries(aliases).forEach(([field, names]) => {
              if (names.includes(norm)) headerMap[i] = field;
            });
          });
          if (!Object.values(headerMap).includes('name')) {
            this.$flash('error', 'Spreadsheet must have a "Name" column.');
            return;
          }
          const parsed = rows
            .slice(1)
            .filter(r => r.some(c => String(c).trim()))
            .map(r => {
              const row = emptyRow();
              Object.entries(headerMap).forEach(([i, field]) => {
                row[field] = String(r[i] || '').trim();
              });
              return row;
            })
            .filter(r => r.name);
          if (!parsed.length) {
            this.$flash('error', 'No valid rows found in file.');
            return;
          }
          const available =
            100 -
            (this.applications.length === 1 && !this.applications[0].name
              ? 0
              : this.applications.length);
          if (parsed.length > available) {
            this.$flash(
              'error',
              `Only ${available} more row(s) can be added (limit 100). File has ${parsed.length}.`,
            );
            return;
          }
          if (this.applications.length === 1 && !this.applications[0].name) {
            this.applications = parsed;
          } else {
            this.applications.push(...parsed);
          }
          this.$flash('success', `${parsed.length} row(s) imported from file.`);
        } catch {
          this.$flash(
            'error',
            'Failed to parse file. Please use a valid .xlsx, .xls, or .csv file.',
          );
        }
      };
      reader.readAsArrayBuffer(file);
      this.$refs.fileInput.value = null;
    },
    parsePaste() {
      this.pasteError = '';
      const fields = [
        'name',
        'description',
        'sub_brand',
        'website_url',
        'support_url',
        'store_url',
        'client_id',
      ];
      const rows = this.pasteText.split('\n').filter(r => r.trim());

      if (!rows.length) {
        this.pasteError = 'No data found. Paste tab-separated rows.';
        return;
      }

      if (rows.length > 100) {
        this.pasteError = 'Maximum 100 rows allowed per paste. Please reduce the number of rows.';
        return;
      }

      const parsed = rows.map(r => {
        const cols = r.split('\t');
        const row = emptyRow();
        fields.forEach((f, i) => {
          if (cols[i]) row[f] = cols[i].trim();
        });
        return row;
      });

      const emptyNames = parsed.filter(r => !r.name);
      if (emptyNames.length) {
        this.pasteError = `${emptyNames.length} row(s) missing a Name. Name is required in the first column.`;
        return;
      }

      const available =
        100 -
        (this.applications.length === 1 && !this.applications[0].name
          ? 0
          : this.applications.length);
      if (parsed.length > available) {
        this.pasteError = `Only ${available} more row(s) can be added (limit 100). You pasted ${parsed.length}.`;
        return;
      }

      if (this.applications.length === 1 && !this.applications[0].name) {
        this.applications = parsed;
      } else {
        this.applications.push(...parsed);
      }
      this.closePaste();
    },
    validateRows() {
      this.validationError = '';
      let valid = true;

      this.applications.forEach(app => {
        app._errors = {};

        if (!app.name || !app.name.trim()) {
          app._errors.name = 'Name is required';
          valid = false;
        } else if (app.name.trim().length > 255) {
          app._errors.name = 'Max 255 characters';
          valid = false;
        } else if (!NAME_REGEX.test(app.name.trim())) {
          app._errors.name = 'Must be valid name format';
          valid = false;
        }

        if (app.description && app.description.trim()) {
          if (app.description.trim().length > 255) {
            app._errors.description = 'Max 255 characters';
            valid = false;
          } else if (!DESC_REGEX.test(app.description.trim())) {
            app._errors.description = 'Must be valid description format';
            valid = false;
          }
        }

        ['website_url', 'support_url', 'store_url'].forEach(f => {
          if (app[f] && app[f].trim()) {
            if (app[f].trim().length > 255) {
              app._errors[f] = 'Max 255 characters';
              valid = false;
            } else if (!URL_REGEX.test(app[f].trim())) {
              app._errors[f] = 'Must start with http:// or https://';
              valid = false;
            }
          }
        });
      });

      // Force reactivity
      this.applications = [...this.applications];

      if (!this.templateId) {
        this.validationError = 'Please select a template.';
        return false;
      }
      if (!this.resolvedOwnerId) {
        this.validationError = 'Please select an owner.';
        return false;
      }
      if (!valid) {
        this.buildErrorSummary();
        return false;
      }

      if (this.applications.length > 100) {
        this.validationError = 'Maximum 100 applications per batch.';
        return false;
      }
      return true;
    },
    buildPayload() {
      return {
        application_template_id: this.templateId,
        applications: this.applications.map(a => {
          const app = { name: a.name.trim() };
          ['description', 'website_url', 'support_url', 'store_url', 'client_id'].forEach(f => {
            if (a[f] && a[f].trim()) app[f] = a[f].trim();
          });
          if (a.ddrs_entity_id) {
            app.ddrs_entity_id = a.ddrs_entity_id;
          } else if (a.sub_brand && a.sub_brand.trim()) {
            app.sub_brand = a.sub_brand.trim();
          }
          return app;
        }),
      };
    },
    formSave() {
      return HTTP.post(
        `/owners/${this.resolvedOwnerId}/applications/bulk_json`,
        this.buildPayload(),
        { timeout: 120000 },
      )
        .then(res => {
          this.resultApps = res.data.created_applications || [];
          this.resultFailed = res.data.failed_applications || [];
          this.resultsDialog = true;

          if (res.data.total_created > 0) {
            this.$flash('success', `${res.data.total_created} application(s) created!`);
          }
          if (res.data.total_failed > 0) {
            this.$flash('error', `${res.data.total_failed} application(s) failed.`);
          }
          this.$emit('saved');
          return res;
        })
        .catch(err => {
          const response = err && err.response;
          const data = response && response.data ? response.data : {};
          const status = response ? response.status : null;

          // Timeout
          if (err && err.code === 'ECONNABORTED') {
            this.formErrors = {
              request: [
                'Request timed out. The server may still be processing — please check before retrying.',
              ],
            };
            return;
          }

          // No response (network error)
          if (!response) {
            this.formErrors = { network: ['Please check your connection and try again.'] };
            return;
          }

          // Has failed_applications — map to inline errors + summary
          if (data.failed_applications && data.failed_applications.length) {
            this.formErrors = {};
            data.failed_applications.forEach(fail => {
              const idx = this.applications.findIndex(
                a => a.name && a.name.trim() === (fail.name || '').trim(),
              );
              if (idx >= 0) {
                const app = this.applications[idx];
                app._errors = app._errors || {};
                app._errors.name = this.friendlyError(fail.error);
              }
            });
            this.applications = [...this.applications];
            this.buildErrorSummary();
            return;
          }

          // Has applications array (Rails-style errors)
          if (data.applications && data.applications.length) {
            this.applyBackendErrors(data.applications);
            return;
          }

          // Has errors object with applications key (Rails validation errors)
          if (data.errors && data.errors.applications && data.errors.applications.length) {
            this.applyBackendErrors(data.errors.applications);
            return;
          }

          // Has errors array — map to inline errors + summary
          if (Array.isArray(data.errors) && data.errors.length) {
            this.applyBackendErrors(data.errors);
            return;
          }

          // Single error string
          if (data.error) {
            this.formErrors = { error: [data.error] };
            return;
          }

          // Status-specific fallbacks
          if (status === 403) {
            this.formErrors = {
              permission: ['You do not have permission to create applications for this owner.'],
            };
            return;
          }
          if (status === 404) {
            this.formErrors = {
              request: ['Owner or template not found. Please verify your selections.'],
            };
            return;
          }

          // Generic fallback
          this.formErrors = { error: ['Something went wrong. Please try again.'] };
        });
    },
    formSubmit(form) {
      if (!this.validateRows()) return;

      if (form?.validate()) {
        this.loading = true;
        this.formErrors = {};
        this.formSave().finally(() => {
          this.loading = false;
        });
      }
    },
    appPath(applicationId) {
      return { name: 'dog.applications.show', params: { applicationId } };
    },
    closeResults() {
      this.resultApps = [];
      this.resultFailed = [];
      this.resultsDialog = false;
      this.formCancel();
    },

    applyBackendErrors(errors) {
      this.formErrors = {};
      errors.forEach((msg, fallbackIdx) => {
        // Try to match "AppName: error message" format
        const colonIdx = msg.indexOf(':');
        let idx = fallbackIdx;
        let errorMsg = msg;

        if (colonIdx > 0) {
          const name = msg.substring(0, colonIdx).trim();
          const found = this.applications.findIndex(a => a.name && a.name.trim() === name);
          if (found >= 0) {
            idx = found;
            errorMsg = msg.substring(colonIdx + 1).trim();
          }
        }

        const app = this.applications[idx];
        if (app) {
          app._errors = app._errors || {};
          app._errors.name = this.friendlyError(errorMsg);
        }
      });
      this.applications = [...this.applications];
      this.buildErrorSummary();
    },
    buildErrorSummary() {
      this.allFailingRows = this.applications
        .map((app, idx) => ({ app, idx }))
        .filter(({ app }) => app._errors && Object.keys(app._errors).length)
        .map(({ app, idx }) => `Row ${idx + 1}${app.name ? ` (${app.name})` : ''}`);
      if (this.allFailingRows.length) {
        const preview = this.allFailingRows.slice(0, 5).join(', ');
        const suffix =
          this.allFailingRows.length > 5 ? ` and ${this.allFailingRows.length - 5} more...` : '';
        this.validationError = `Errors in: ${preview}${suffix}`;
      }
    },
    friendlyError(msg) {
      if (!msg) return 'An unknown error occurred.';
      if (msg.includes('do not match a valid DDRS entity')) {
        return 'The Client ID and VC-ID do not match a valid Sub-brand. Please correct them.';
      }
      if (msg.includes('client_id and sub_brand') || msg.includes('ddrs_entity_id')) {
        return 'Please add both Client ID and VC-ID, or select a Sub-brand from the dropdown.';
      }
      if (msg.includes('DDRS entity') || msg.includes('Requires DDRS')) {
        return 'Requires DDRS entity. Please select a Sub-brand from dropdown or provide both Client ID and VC-ID.';
      }
      return msg;
    },
    formReady() {
      this.templateId = null;
      this.selectedOwnerId = null;
      this.applications = [emptyRow()];
      this.validationError = '';
    },
  },
};
</script>
